import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, CalendarBlank, ArrowsClockwise } from '@phosphor-icons/react';
import { labelClass, riskClass, fmtD } from '../../components/admin/planning/planningUtils';

const COLOR_CELL = {
  green: 'bg-emerald-100 border-emerald-300',
  blue: 'bg-blue-100 border-blue-300',
  orange: 'bg-amber-100 border-amber-300',
  red: 'bg-red-100 border-red-400',
  grey: 'bg-zinc-200 border-zinc-300',
};

const STATUS_OPTIONS = [
  ['', 'All Statuses'], ['pending_procurement', 'Pending Procurement'],
  ['procurement_approved', 'Procurement Approved'], ['assigned_to_production', 'Assigned to Production'],
  ['under_production', 'Under Production'], ['production_completed', 'Production Completed'],
  ['ready_to_dispatch', 'Ready to Dispatch'], ['dispatched', 'Dispatched'],
  ['installation_completed', 'Installation Completed'],
];

function monthRange(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(first), to: iso(last) };
}

export default function DeliveryCalendar() {
  const [anchor, setAnchor] = useState(new Date());
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ executive: '', customer: '', status: '' });
  const [selectedDay, setSelectedDay] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = monthRange(anchor);
      const params = new URLSearchParams({ from_date: from, to_date: to });
      if (filters.executive) params.set('executive', filters.executive);
      if (filters.customer) params.set('customer', filters.customer);
      if (filters.status) params.set('status', filters.status);
      const { data } = await api.get(`/api/admin/production-planning/delivery-calendar?${params}`);
      setDays(data.days || []);
    } catch (e) { toast.error('Failed to load delivery calendar'); }
    setLoading(false);
  }, [anchor, filters]);

  useEffect(() => { load(); }, [load]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post('/api/admin/production-planning/refresh');
      toast.success(`Re-allocated ${r.replanned} job orders${r.forced_qty > 0 ? ` (${r.forced_qty} units force-fitted to earliest dates)` : ''}`);
      await load();
    } catch (e) { toast.error('Refresh failed'); }
    setRefreshing(false);
  };

  const monthLabel = anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const firstDow = days.length ? (new Date(days[0].date).getDay() + 6) % 7 : 0;
  const totals = days.reduce((a, d) => ({
    jos: a.jos + d.jo_count, qty: a.qty + d.total_qty, produced: a.produced + d.produced_qty,
    pending: a.pending + d.pending_qty, delayed: a.delayed + d.delayed_qty,
  }), { jos: 0, qty: 0, produced: 0, pending: 0, delayed: 0 });

  return (
    <Layout>
      <div className="space-y-4" data-testid="delivery-calendar-page">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <CalendarBlank size={26} /> Delivery Due Calendar
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={doRefresh} disabled={refreshing} data-testid="dc-refresh-btn"
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
              <ArrowsClockwise size={16} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Re-allocating…' : 'Refresh & Re-allocate'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/production-planning')} data-testid="goto-planning-btn">
              Production Calendar →
            </Button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-5 gap-2">
          {[['Due JOs', totals.jos], ['Total Qty', totals.qty], ['Produced', totals.produced],
            ['Pending', totals.pending], ['Delayed', totals.delayed]].map(([k, v]) => (
            <div key={k} className={`p-2.5 rounded-sm border text-left ${k === 'Delayed' && v > 0 ? 'border-red-300 bg-red-50' : 'border-zinc-200 bg-white'}`}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k} (month)</div>
              <div className="text-xl font-bold">{v}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Executive</div>
            <Input className="h-8 w-44 text-xs" value={filters.executive} onChange={(e) => setFilters({ ...filters, executive: e.target.value })} data-testid="filter-executive" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Customer</div>
            <Input className="h-8 w-44 text-xs" value={filters.customer} onChange={(e) => setFilters({ ...filters, customer: e.target.value })} data-testid="filter-customer" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">JO Status</div>
            <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
              <SelectTrigger className="h-8 w-52 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {STATUS_OPTIONS.map(([v, l]) => <SelectItem key={v || 'all'} value={v || 'all'}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Month nav */}
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))} data-testid="dc-prev-month"><CaretLeft size={16} /></Button>
          <div className="text-lg font-bold">{monthLabel}</div>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))} data-testid="dc-next-month"><CaretRight size={16} /></Button>
        </div>

        {loading ? <div className="p-10 text-center text-zinc-400">Loading…</div> : (
          <div className="grid grid-cols-7 gap-1" data-testid="delivery-calendar-grid">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-wider text-zinc-500 text-center font-bold py-1">{d}</div>
            ))}
            {Array.from({ length: firstDow }).map((_, i) => <div key={`pad-${i}`} />)}
            {days.map((d) => (
              <button key={d.date}
                onClick={() => d.jo_count > 0 && setSelectedDay(d)}
                className={`min-h-[76px] p-1.5 rounded-sm border text-left ${d.jo_count > 0 ? `cursor-pointer hover:ring-2 hover:ring-blue-300 ${COLOR_CELL[d.color] || 'bg-white border-zinc-200'}` : 'bg-white border-zinc-100'}`}
                data-testid={`dc-day-${d.date}`}>
                <div className="text-sm font-bold">{d.date.slice(-2)}</div>
                {d.jo_count > 0 && (
                  <div className="text-[9px] font-mono space-y-0.5">
                    <div className="font-bold">{d.jo_count} JO · {d.total_qty} qty</div>
                    <div className="text-zinc-600">Done {d.produced_qty} · Pend {d.pending_qty}</div>
                    {d.delayed_qty > 0 && <div className="text-red-700 font-bold">Delayed {d.delayed_qty}</div>}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-3 text-[10px] text-zinc-500 flex-wrap">
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-emerald-100 border border-emerald-300 mr-1" />Completed / Dispatched / Installed</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-blue-100 border border-blue-300 mr-1" />In production, on schedule</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-amber-100 border border-amber-300 mr-1" />Pending, achievable</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-red-100 border border-red-400 mr-1" />Delivery risk / overdue</span>
        </div>
      </div>

      {/* Day drill-down */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-5xl bg-white max-h-[85vh] overflow-y-auto" data-testid="delivery-day-dialog">
          <DialogHeader><DialogTitle>Deliveries Due — {selectedDay && fmtD(selectedDay.date)}</DialogTitle></DialogHeader>
          {selectedDay && (
            <div className="space-y-3">
              <div className="grid grid-cols-6 gap-2">
                {[['Due JOs', selectedDay.jo_count], ['Total Qty', selectedDay.total_qty],
                  ['Produced', selectedDay.produced_qty], ['Pending', selectedDay.pending_qty],
                  ['Dispatched', selectedDay.dispatched_qty], ['Delayed', selectedDay.delayed_qty]].map(([k, v]) => (
                  <div key={k} className="p-2 rounded-sm border border-zinc-200 bg-zinc-50 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
                    <div className="text-lg font-bold">{v}</div>
                  </div>
                ))}
              </div>
              <table className="min-w-full text-xs">
                <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left">JO No</th>
                    <th className="p-2 text-left">Customer</th>
                    <th className="p-2 text-left">Executive</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Procurement</th>
                    <th className="p-2 text-left">Prod Window</th>
                    <th className="p-2 text-right">Remaining</th>
                    <th className="p-2 text-left">Risk</th>
                    <th className="p-2 text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDay.jos.map((jo) => (
                    <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`dc-jo-${jo.jo_number}`}>
                      <td className="p-2 font-mono font-bold text-blue-700">{jo.jo_number}</td>
                      <td className="p-2">{jo.customer_name}</td>
                      <td className="p-2">{jo.executive_name}</td>
                      <td className="p-2 text-right font-bold">{jo.total_qty}</td>
                      <td className="p-2"><Badge className={`text-[10px] border ${labelClass(jo.allocation_label)}`}>{jo.allocation_label}</Badge></td>
                      <td className="p-2 text-[10px]">{jo.procurement_status}</td>
                      <td className="p-2 whitespace-nowrap">{fmtD(jo.production_start)} → {fmtD(jo.production_end)}</td>
                      <td className="p-2 text-right">{jo.remaining_qty}</td>
                      <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${riskClass(jo.risk)}`}>{jo.risk}</span></td>
                      <td className="p-2">
                        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => navigate(`/job-orders/${jo.jo_number}`)}>Open JO</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
